import { IPgnMove } from '@libs/chess/pgn';

import { findMovePosition } from './moves';

// Навигация
//
// Правила:
// - first указывает на "исходную" позицию текущего варианта:
//   - для главной ветки: null (доска до 1 хода)
//   - для подварианта: последний не пустой (null move) ход перед началом этого варианта
// - prev по-умолчанию null, что позволяет перевести доску в "исходное" состояние
// - next и last не дают выйти за пределы текущего варианта
// - alts — альтернативы next
// - виртуальные ветки (когда части одного варианта отображаются как ветви
//   дерева вместе с настоящими вариантами) требуют дополнительной логики
// - пустые ходы (null moves: '--') пропускаем для next/prev/first; last не пропускаем

type IResult = {
  first?: IPgnMove | null;
  last?: IPgnMove | null;
  next?: IPgnMove;
  prev?: IPgnMove | null;
  alts?: IPgnMove[];
  playSequence?: IPgnMove[];
};

export function getNavigationMoves(selectedId: string | undefined, moves: IPgnMove[]): IResult {
  // Defaults (если нет выбранного)

  let first: IPgnMove | undefined | null = undefined;
  let last: IPgnMove | undefined = moves[moves.length - 1];
  let next: IPgnMove | undefined = moves[0];
  let prev: IPgnMove | undefined | null = undefined;
  let alts: IPgnMove[] | undefined;
  let playSequence: IPgnMove[] | undefined;

  // Поиск (если есть выбранный)

  if (selectedId) {
    const position = findMovePosition(selectedId, moves);
    const path = position?.path;
    if (!path) {
      console.error('PgnEditor: getNavigationMoves: current selected move not found in moves', {
        selectedId,
        moves,
      });
      return {};
    }

    playSequence = position.playSequence;

    next = position.next;
    prev = position.prev;

    const { variant } = position;

    // Пропуск null move

    // next: вперёд по текущему варианту
    if (next?.san === '--') {
      next = undefined;
      for (let i = position.index + 2; i < variant.length; i++) {
        if (variant[i]!.san !== '--') {
          next = variant[i];
          break;
        }
      }
    }

    // prev: назад по playSequence (пересекает границы вариантов)
    if (prev?.san === '--') {
      prev = null;
      for (let j = playSequence.length - 3; j >= 0; j--) {
        if (playSequence[j]!.san !== '--') {
          prev = playSequence[j];
          break;
        }
      }
    }

    // first: последний non-null ход перед началом текущего варианта
    if (variant === moves) {
      first = null;
    } else {
      let k = playSequence.length - 2 - position.index;
      while (k >= 0 && playSequence[k]!.san === '--') k--;
      first = k >= 0 ? playSequence[k] : null;
    }

    // alts: альтернативы next (выбор до развилки)
    alts = getAltsFromNext(next);

    last = variant[variant.length - 1];

    // Виртуальные ветки
    // Хвост текущего варианта "отрезается" и отображается как отдельный подвариант (виртуальная ветка)

    if (variant !== moves) {
      const getIsBranched = (m?: IPgnMove) => m?.alts && m.alts.length > 1;

      if (next && getIsBranched(next)) {
        // next = undefined;
        last = undefined;
      }

      const virtualLastIndex = variant.findIndex((m, i) => i > position.index && getIsBranched(m));
      if (virtualLastIndex !== -1) {
        last = variant[virtualLastIndex - 1];
      }

      let virtualFirstIndex = -1;
      for (let i = position.index; i >= 0; i--) {
        if (getIsBranched(variant[i])) {
          virtualFirstIndex = i;
          break;
        }
      }
      if (virtualFirstIndex !== -1) {
        first = variant[virtualFirstIndex - 1];
      }
    }
  } else {
    // Defaults: next указывает на moves[0] — пропустим если null
    if (next?.san === '--') {
      next = moves.find((m) => m.san !== '--');
    }

    alts = getAltsFromNext(next);
  }

  // Нормализация

  if (last?.id === selectedId) last = undefined;

  return { first, last, next, prev, alts, playSequence };
}

function getAltsFromNext(next: IPgnMove | undefined): IPgnMove[] | undefined {
  if (!next) return undefined;

  const list = next.alts
    ?.map((v) => v.moves.find((m) => m.san !== '--'))
    .filter((m): m is IPgnMove => !!m);

  if (!list?.length) return undefined;

  return list;
}
